import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wrench, Search, ChevronRight, CheckCircle2, PlayCircle, CircleDashed, Archive } from "lucide-react";
import { ColorChip } from "@/components/ColorChip";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("de-DE");

export const STATUS_META = {
  angelegt:      { label: "Angelegt", Icon: CircleDashed, cls: "border border-amber-200 bg-amber-50 text-amber-700" },
  in_bearbeitung:{ label: "In Bearbeitung", Icon: PlayCircle, cls: "border border-blue-200 bg-blue-50 text-blue-700" },
  fertig:        { label: "Abgeschlossen", Icon: CheckCircle2, cls: "border border-emerald-200 bg-emerald-50 text-emerald-700" },
  archiviert:    { label: "Archiviert", Icon: Archive, cls: "border border-slate-200 bg-slate-100 text-slate-600" },
};

const FILTERS = [
  { id: "all", label: "Alle", value: null },
  { id: "angelegt", label: "Angelegt", value: "angelegt" },
  { id: "in_bearbeitung", label: "In Bearbeitung", value: "in_bearbeitung" },
  { id: "fertig", label: "Abgeschlossen", value: "fertig" },
];

export default function Jobs() {
  const [statusFilter, setStatusFilter] = useState(null);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);

  const { data = [] } = useQuery({
    queryKey: ["jobs", statusFilter, includeArchived],
    queryFn: async () => (await api.get("/jobs", { params: { status: statusFilter || undefined, include_archived: includeArchived }})).data,
  });

  const filtered = q
    ? data.filter((j) => {
        const s = q.toLowerCase();
        return (j.customer_name || "").toLowerCase().includes(s)
            || (j.vehicle_display || "").toLowerCase().includes(s)
            || (j.title || "").toLowerCase().includes(s);
      })
    : data;

  return (
    <div className="stellar-page stellar-jobs-page">
      <div className="stellar-page-heading flex flex-wrap items-center gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="stellar-module-icon stellar-module-green"><Wrench /></div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">Aufträge</h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Werkstatt-Aufträge und deren Bearbeitungsstatus.</p>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-full sm:w-64 bg-white" data-testid="job-search" />
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="job-include-archived" />
            Archiv anzeigen
          </label>
        </div>
      </div>

      <div className="stellar-segmented mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.value)}
            data-testid={`job-filter-${f.id}`}
            className={`px-3.5 py-1.5 text-sm rounded-lg whitespace-nowrap transition ${statusFilter === f.value ? "bg-white text-emerald-700 shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="premium-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground">
            <Wrench className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <div className="mb-1">Keine Aufträge gefunden.</div>
            <div className="text-xs">Aufträge werden durch Umwandlung eines Termins oder direkt angelegt.</div>
          </div>
        ) : (
          <table className="stellar-data-table w-full text-sm">
            <thead className="bg-slate-50/80 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Auftrag</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Kunde</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Fahrzeug</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Summe</th>
                <th className="text-right px-4 py-3 hidden lg:table-cell">Erstellt</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map((j) => {
                const st = STATUS_META[j.status] || STATUS_META.angelegt;
                return (
                  <tr key={j.id} className="stellar-table-row">
                    <td className="px-4 py-3">
                      <Link to={`/auftraege/${j.id}`} data-testid={`job-row-${j.id}`} className="font-medium hover:underline">
                        {j.title || "Auftrag"}
                      </Link>
                      <div className="text-xs text-muted-foreground md:hidden">{j.customer_name} · {j.vehicle_display}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{j.customer_name || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">
                      <span>{j.vehicle_display || "—"}</span>
                      {j.vehicle_color && <> · <ColorChip value={j.vehicle_color} /></>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${st.cls}`}>
                        <st.Icon className="h-3 w-3" /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{fmtEur(j.total_price)}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground text-xs hidden lg:table-cell">{fmtDate(j.created_at)}</td>
                    <td className="px-2"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
